// SpecsEditor — faithful port of sampler/src/components/SpecsEditor.vue, adapted
// for PEXIFY: Material is a SINGLE-SELECT dropdown pinned to the top (always
// present, no "+ Material" chip), above the color rows. The remaining typed
// chips (+ Color / + Size / + Custom) still add colored spec rows edited inline.
// onChange emits the full next array (the parent debounce-autosaves the brief).

import { useEffect, useRef, useState } from 'react'
import { Check, Pencil, Trash2 } from 'lucide-react'
import { MATERIAL_OPTIONS, type BriefSpec } from './lib'
import { useT } from '../../i18n'
import { specsEditorI18n, type SpecsEditorStrings } from '../../i18n/samples/specs-editor'

const TYPES: Array<'color' | 'size' | 'custom'> = ['color', 'size', 'custom']

const SIZE_OPTIONS = ['Standard', 'X-Small', 'Small', 'Medium', 'Large', 'X-Large']
const TYPE_ORDER = ['color', 'size', 'custom']

const COLORS: Record<string, string> = {
  color: 'bg-[var(--color-surface-muted)] text-[var(--color-info)] border-[var(--color-border)]',
  size: 'bg-[var(--color-surface-muted)] text-[var(--color-info)] border-[var(--color-border)]',
  custom: 'bg-[var(--color-surface-muted)] text-[var(--color-text)] border-[var(--color-border)]',
}

function placeholderFor(type: string, t: SpecsEditorStrings): string {
  if (type === 'color') return t.placeholders.color
  if (type === 'size') return t.placeholders.size
  return t.placeholders.other
}

function sizeOptionsFor(spec: BriefSpec): string[] {
  const current = spec.label || 'Standard'
  return SIZE_OPTIONS.includes(current) ? SIZE_OPTIONS : [current, ...SIZE_OPTIONS]
}

function materialOptionsFor(current: string): string[] {
  const opts = [...MATERIAL_OPTIONS] as string[]
  return current && !opts.includes(current) ? [current, ...opts] : opts
}

// One-line display for a spec, used by the factory's collapsed (read-only) view.
function specDisplay(s: BriefSpec): string {
  const lbl = (s.label || '').trim()
  const val = (s.value || '').trim()
  if (s.type === 'size') return val ? (lbl ? `${lbl} (${val})` : val) : lbl
  if (s.type === 'custom') return lbl && val ? `${lbl}: ${val}` : (val || lbl)
  return val // color (and any other value-only type)
}

export function SpecsEditor({
  specs,
  disabled,
  onChange,
  restrictToMaterial = false,
  onMaterialChange,
}: {
  specs: BriefSpec[]
  disabled: boolean
  onChange: (next: BriefSpec[]) => void
  // Factory view: Color/Size/Custom are the customer's to author — read-only
  // here (no add/edit/remove). Material stays editable (the factory may set it
  // until we standardise it). `disabled` (full lock) still wins over this.
  restrictToMaterial?: boolean
  // When set, a material change is routed here (the factory's dedicated material
  // endpoint) instead of the full-brief onChange.
  onMaterialChange?: (value: string) => void
}) {
  const t = useT(specsEditorI18n)
  const [editingIdx, setEditingIdx] = useState<number | null>(null)
  const valueRef = useRef<HTMLInputElement | null>(null)
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const typedEditable = !disabled && !restrictToMaterial

  useEffect(() => () => { if (focusTimerRef.current) clearTimeout(focusTimerRef.current) }, [])

  const materialValue = specs.find((s) => s.type === 'material')?.value || ''

  function setMaterial(value: string) {
    if (onMaterialChange) {
      onMaterialChange(value)
      return
    }
    const rest = specs.filter((s) => s.type !== 'material')
    onChange(value ? [{ type: 'material', value }, ...rest] : rest)
  }

  function add(type: string) {
    const next = [...specs, { type, label: type === 'size' ? 'Standard' : '', value: '' }]
    onChange(next)
    setEditingIdx(next.length - 1)
    if (focusTimerRef.current) clearTimeout(focusTimerRef.current)
    focusTimerRef.current = setTimeout(() => valueRef.current?.focus(), 0)
  }

  function update(i: number, patch: Partial<BriefSpec>) {
    const next = specs.map((s, idx) => (idx === i ? { ...s, ...patch } : s))
    onChange(next)
  }

  function remove(i: number) {
    onChange(specs.filter((_, idx) => idx !== i))
    setEditingIdx((cur) => (cur === i ? null : cur !== null && cur > i ? cur - 1 : cur))
  }

  function startEdit(i: number) {
    setEditingIdx(i)
    if (focusTimerRef.current) clearTimeout(focusTimerRef.current)
    focusTimerRef.current = setTimeout(() => valueRef.current?.focus(), 0)
  }

  function cancelOrRemoveIfEmpty(i: number, s: BriefSpec) {
    if ((!s.value && !s.label && s.type !== 'custom') || (s.type === 'custom' && !s.label && !s.value)) {
      remove(i)
    }
    setEditingIdx((cur) => (cur === i ? null : cur))
  }

  // Sort the editable (non-material) rows by type order, then original index;
  // keep the real index for edits. Material is rendered separately above.
  const grouped = specs
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => s.type !== 'material')
    .sort((a, b) => {
      const ar = TYPE_ORDER.indexOf(a.s.type) === -1 ? TYPE_ORDER.length : TYPE_ORDER.indexOf(a.s.type)
      const br = TYPE_ORDER.indexOf(b.s.type) === -1 ? TYPE_ORDER.length : TYPE_ORDER.indexOf(b.s.type)
      return ar !== br ? ar - br : a.i - b.i
    })

  // For the factory's collapsed view: same-type specs gathered under one row
  // (order follows `grouped`, i.e. TYPE_ORDER).
  const collapsedMap = new Map<string, BriefSpec[]>()
  for (const { s } of grouped) {
    const bucket = collapsedMap.get(s.type)
    if (bucket) bucket.push(s)
    else collapsedMap.set(s.type, [s])
  }
  const collapsedGroups = [...collapsedMap.entries()]

  return (
    <div className="space-y-2">
      {/* Material — always-present single-select dropdown, pinned to the top */}
      {(!disabled || materialValue) && (
        <div className="flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-2 py-1.5">
          <span className="rounded-full bg-[var(--color-surface)]/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-success)]">
            {t.material}
          </span>
          {disabled ? (
            <span className="text-sm font-medium text-[var(--color-success)]">{materialValue || '—'}</span>
          ) : (
            <select
              value={materialValue}
              onChange={(e) => setMaterial(e.target.value)}
              aria-label={t.material}
              className="flex-1 cursor-pointer rounded bg-transparent text-sm font-medium text-[var(--color-success)] outline-none focus:ring-2 focus:ring-emerald-300"
            >
              <option value="">{t.selectMaterial}</option>
              {materialOptionsFor(materialValue).map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {typedEditable && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">{t.add}</span>
          <div className="flex flex-wrap gap-1.5">
            {TYPES.map((ty) => (
              <button
                key={ty}
                type="button"
                onClick={() => add(ty)}
                className="rounded-full bg-[var(--color-surface-muted)] px-2.5 py-1 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-border)]"
              >
                + {t.types[ty]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Factory (read-only): collapse multiple specs of the same type into one
          row — e.g. COLOR  Black · Tan · Cognac. The buyer keeps separate rows. */}
      {restrictToMaterial && collapsedGroups.length > 0 && (
        <ul className="space-y-1.5">
          {collapsedGroups.map(([type, items]) => {
            const joined = items.map(specDisplay).filter(Boolean).join('  ·  ')
            return (
              <li
                key={type}
                className={`flex items-center gap-2 rounded-lg border bg-[var(--color-surface)] px-2 py-1.5 ${COLORS[type] || COLORS.custom}`}
              >
                <span className="rounded-full bg-[var(--color-surface)]/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                  {t.typeChip(type)}
                </span>
                <span className={`flex-1 text-sm ${joined ? '' : 'italic text-[var(--color-text-muted)]'}`}>{joined || '—'}</span>
              </li>
            )
          })}
        </ul>
      )}

      {!restrictToMaterial && grouped.length > 0 && (
        <ul className="space-y-1.5">
          {grouped.map(({ s, i }) => {
            const editing = typedEditable && editingIdx === i
            return (
              <li
                key={i}
                className={`group flex items-center gap-2 rounded-lg border bg-[var(--color-surface)] px-2 py-1.5 ${COLORS[s.type] || COLORS.custom}`}
              >
                <span className="rounded-full bg-[var(--color-surface)]/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                  {t.typeChip(s.type)}
                </span>

                {!editing ? (
                  <>
                    {(s.type === 'size' || s.type === 'custom') && (
                      <>
                        <span className="text-sm font-medium">{s.type === 'custom' ? s.label || '—' : s.label}</span>
                        <span className="text-[var(--color-text-muted)]">·</span>
                      </>
                    )}
                    <span className={`flex-1 text-sm ${s.value ? '' : 'italic text-[var(--color-text-muted)]'}`}>{s.value || '—'}</span>
                    {typedEditable && (
                      <div className="flex items-center gap-0.5 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100">
                        <button type="button" title={t.edit(s.type)} onClick={() => startEdit(i)}
                          className="grid h-6 w-6 place-items-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]">
                          <Pencil size={12} />
                        </button>
                        <button type="button" title={t.remove(s.type)} onClick={() => remove(i)}
                          className="grid h-6 w-6 place-items-center rounded text-[var(--color-text-muted)] hover:bg-rose-50 hover:text-rose-600">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {s.type === 'size' && (
                      <>
                        <select
                          value={s.label || 'Standard'}
                          onChange={(e) => update(i, { label: e.target.value })}
                          className="w-28 cursor-pointer rounded bg-transparent text-sm font-medium outline-none focus:ring-2 focus:ring-blue-200"
                        >
                          {sizeOptionsFor(s).map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                        <span className="text-[var(--color-text-muted)]">·</span>
                      </>
                    )}
                    {s.type === 'custom' && (
                      <>
                        <input
                          className="w-32 bg-transparent text-sm font-medium outline-none placeholder:text-[var(--color-text-muted)]"
                          placeholder={t.label} value={s.label || ''}
                          onChange={(e) => update(i, { label: e.target.value })}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setEditingIdx(null) } else if (e.key === 'Escape') { e.preventDefault(); cancelOrRemoveIfEmpty(i, s) } }}
                        />
                        <span className="text-[var(--color-text-muted)]">·</span>
                      </>
                    )}
                    <input
                      ref={valueRef}
                      className="flex-1 bg-transparent text-sm outline-none placeholder:text-[var(--color-text-muted)]"
                      placeholder={placeholderFor(s.type, t)} value={s.value || ''}
                      onChange={(e) => update(i, { value: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setEditingIdx(null) } else if (e.key === 'Escape') { e.preventDefault(); cancelOrRemoveIfEmpty(i, s) } }}
                    />
                    <button type="button" title={t.save} onMouseDown={(e) => { e.preventDefault(); setEditingIdx(null) }}
                      className="grid h-6 w-6 place-items-center rounded text-emerald-600 hover:bg-emerald-50">
                      <Check size={14} />
                    </button>
                    <button type="button" title={t.removeRow} onMouseDown={(e) => { e.preventDefault(); remove(i) }}
                      className="grid h-6 w-6 place-items-center rounded text-[var(--color-text-muted)] hover:bg-rose-50 hover:text-rose-600">
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
